/**
 * FormulaFieldType — F12.B T12.B.4.
 * Pole read-only obliczane z wyrażenia tekstowego (np. `cena * ilosc`).
 * Wyrażenie przechowywane w `field.validationRules.formula_expression`.
 * Obliczenia: prosta ewaluacja po stronie klienta; pełen engine w F12.C.
 */
import { useEffect, useState } from 'react';
import { Calculator } from 'lucide-react';

import type { ModuleField } from '../FieldRegistry';
import { FieldRegistry } from '../FieldRegistry';

function timeToMinutes(v: unknown): number | null {
  if (typeof v !== 'string') return null;
  const m = v.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return parseInt(m[1]!, 10) * 60 + parseInt(m[2]!, 10);
}

function evaluate(
  expr: string,
  formData: Record<string, unknown>,
  fields: ModuleField[],
): { result: unknown; error: string | null } {
  try {
    const typeMap: Record<string, string> = {};
    for (const f of fields) typeMap[f.slug] = f.fieldType;

    let processed = expr;
    const refs = expr.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) ?? [];
    for (const slug of refs) {
      const raw = formData[slug];
      const ft = typeMap[slug];
      let num: number;
      if (ft === 'time') {
        num = timeToMinutes(raw) ?? 0;
      } else {
        num = raw != null ? Number(raw) : 0;
      }
      if (isNaN(num)) return { result: null, error: `Pole "${slug}" nie ma wartości numerycznej` };
      processed = processed.replace(new RegExp(`\\b${slug}\\b`, 'g'), String(num));
    }

    if (!/^[\d\s+\-*/.()]+$/.test(processed))
      return { result: null, error: 'Nieprawidłowe znaki w formule' };

    const result = eval(processed);
    return { result, error: null };
  } catch (e) {
    return { result: null, error: (e as Error).message };
  }
}

FieldRegistry.register({
  type: 'formula',
  renderInput: ({ field, value, onChange, formData = {}, fields = [] }) => {
    const expr = (field.validationRules?.['formula_expression'] as string | undefined) ?? '';
    const [result, setResult] = useState<unknown>(value ?? null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
      if (!expr) {
        setErr('Formuła nie została zdefiniowana');
        return;
      }
      const { result: r, error } = evaluate(expr, formData, fields);
      setErr(error);
      setResult(r);
      if (!error && r !== value) onChange(r);
    }, [expr, JSON.stringify(formData)]);

    return (
      <div className="space-y-2">
        <div className="rounded-lg border border-accent-200 bg-accent-50 px-3 py-2">
          <div className="mb-1 flex items-center gap-1 text-xs font-medium text-accent-600">
            <Calculator className="h-3 w-3" />
            Formuła: <code className="ml-1">{expr || 'brak'}</code>
          </div>
          {err ? (
            <p className="text-sm text-danger-600">{err}</p>
          ) : (
            <p className="text-2xl font-bold text-accent-900">
              {result != null
                ? typeof result === 'number'
                  ? result.toLocaleString('pl-PL', { maximumFractionDigits: 2 })
                  : String(result)
                : '—'}
            </p>
          )}
        </div>
        <p className="text-xs text-text-muted">Pole obliczane automatycznie. Tylko do odczytu.</p>
      </div>
    );
  },
  renderCell: (field, value) => {
    if (value == null) return '—';
    const expr = (field.validationRules?.['formula_expression'] as string | undefined) ?? '';
    return (
      <div className="flex items-center gap-2">
        <Calculator className="h-3.5 w-3.5 flex-shrink-0 text-accent-500" />
        <div>
          <div className="text-sm font-semibold text-text-primary">
            {typeof value === 'number'
              ? value.toLocaleString('pl-PL', { maximumFractionDigits: 2 })
              : String(value)}
          </div>
          <div className="font-mono text-xs text-text-muted">{expr}</div>
        </div>
      </div>
    );
  },
  validate: () => null,
  defaultValue: null,
});
